<template>
  <div class="hpc-card">
    <div class="hpc-bg-glow"></div>

    <div class="hpc-row">
      <!-- Coluna esquerda: distribui em 3 blocos (top/center/bottom) pra
           preencher a altura sem espaço vazio. -->
      <div class="hpc-now">
        <div class="hpc-label-row">
          <span class="hpc-live-dot"></span>
          <span class="hpc-label">CHECK-INS REALIZADOS</span>
          <span v-if="isFairMode" class="hpc-mode-pill" :title="`Estimativa baseada em check-ins dos últimos ${dwellMinutes} minutos`">
            <i class="bx bx-bulb"></i>
            modo feira · {{ dwellMinutes }}min
          </span>
        </div>

        <div class="hpc-value-block">
          <div class="hpc-value-line">
            <div class="hpc-value" :class="{ 'hpc-value-bump': bump }">
              {{ animatedAttended }}
            </div>
            <div class="hpc-fraction">
              <span class="hpc-fraction-divider">/</span>
              <span class="hpc-fraction-total">{{ totalConfirmed }}</span>
              <span class="hpc-fraction-label">confirmados</span>
            </div>
          </div>

          <!-- Barra fina mostrando progresso (mesma info do gauge, mas inline). -->
          <div v-if="attendance && totalConfirmed > 0" class="hpc-progress">
            <div
              class="hpc-progress-fill"
              :style="{ width: Math.min(100, attendance.rate_pct) + '%' }"
            ></div>
          </div>
        </div>

        <div class="hpc-stats">
          <div class="hpc-stat">
            <span class="hpc-stat-icon hpc-stat-icon-velocity">
              <i class="bx bx-trending-up"></i>
            </span>
            <div class="hpc-stat-body">
              <div class="hpc-stat-value">
                <template v-if="velocityPerMin !== null && velocityPerMin > 0">
                  {{ velocityPerMin }}<small>/min</small>
                </template>
                <template v-else>—</template>
              </div>
              <div class="hpc-stat-label">velocidade</div>
            </div>
          </div>

          <div class="hpc-stat" v-if="insideNow !== null && insideNow !== undefined">
            <span class="hpc-stat-icon hpc-stat-icon-inside">
              <i :class="isFairMode ? 'bx bx-walk' : 'bx bx-user-check'"></i>
            </span>
            <div class="hpc-stat-body">
              <div class="hpc-stat-value">
                <span v-if="isFairMode" class="hpc-inside-prefix">≈</span>{{ insideNow }}
              </div>
              <div class="hpc-stat-label">{{ isFairMode ? 'circulando' : 'dentro agora' }}</div>
            </div>
          </div>

          <div class="hpc-stat" v-if="lastReadAt">
            <span class="hpc-stat-icon hpc-stat-icon-time">
              <i class="bx bx-time-five"></i>
            </span>
            <div class="hpc-stat-body">
              <div class="hpc-stat-value hpc-stat-value-text">
                {{ formatLastRead(lastReadAt) }}
              </div>
              <div class="hpc-stat-label">última leitura</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Coluna direita: gauge de taxa de comparecimento (padrão /dashboard) -->
      <div class="hpc-gauge">
        <apexchart
          v-if="attendance"
          type="radialBar"
          :options="gaugeOptions"
          :series="gaugeSeries"
          height="200"
          width="220"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'hero-pulse-card',
  props: {
    insideNow: { type: Number, default: 0 },
    velocityPerMin: { type: Number, default: null },
    attendance: { type: Object, default: null },
    insideNowMode: { type: String, default: 'last_event' },
    dwellMinutes: { type: Number, default: 15 },
    lastReadAt: { type: String, default: null }
  },
  data: () => ({
    animatedAttended: 0,
    bump: false,
    nowTick: 0,
    nowTickInterval: null
  }),
  computed: {
    isFairMode () {
      return this.insideNowMode === 'fair_dwell'
    },
    totalAttended () {
      return this.attendance && this.attendance.total_attended != null
        ? this.attendance.total_attended
        : 0
    },
    totalConfirmed () {
      return this.attendance && this.attendance.total_confirmed != null
        ? this.attendance.total_confirmed
        : 0
    },
    gaugeSeries () {
      return [this.attendance ? this.attendance.rate_pct : 0]
    },
    gaugeOptions () {
      return {
        chart: {
          type: 'radialBar',
          toolbar: { show: false },
          sparkline: { enabled: true },
          animations: { enabled: true, easing: 'easeinout', speed: 700 }
        },
        plotOptions: {
          radialBar: {
            // Mesmo arco aberto do gauge de Meta no /dashboard pra não
            // sobrepor o label central ao "vazado".
            startAngle: -135,
            endAngle: 135,
            hollow: {
              size: '62%',
              background: 'transparent'
            },
            track: {
              background: '#eef0fb',
              strokeWidth: '100%',
              margin: 0
            },
            dataLabels: {
              name: {
                show: true,
                fontSize: '11px',
                fontWeight: 600,
                color: '#8b90a5',
                offsetY: 26,
                formatter: () => 'comparecimento'
              },
              value: {
                fontSize: '26px',
                fontWeight: 800,
                color: '#1a1d26',
                offsetY: -4,
                formatter: (val) => `${val}%`
              }
            }
          }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'light',
            type: 'horizontal',
            shadeIntensity: 0,
            gradientToColors: ['#34c38f'],
            stops: [0, 100]
          }
        },
        colors: ['#6366f1'],
        stroke: { lineCap: 'round' },
        labels: ['Comparecimento']
      }
    }
  },
  watch: {
    totalAttended: {
      immediate: true,
      handler (newVal, oldVal) {
        this.animateTo(newVal, oldVal || 0)
        if (oldVal !== undefined && newVal > oldVal) {
          this.triggerBump()
        }
      }
    }
  },
  mounted () {
    this.nowTickInterval = setInterval(() => { this.nowTick++ }, 30000)
  },
  beforeDestroy () {
    if (this.nowTickInterval) clearInterval(this.nowTickInterval)
  },
  methods: {
    /**
     * "última leitura há Xmin" — suaviza o card velocidade quando velocity=0
     * (em vez de mostrar "0 leit/min" sem contexto).
     */
    formatLastRead (datetime) {
      // eslint-disable-next-line no-unused-expressions
      this.nowTick
      if (!datetime) return ''
      const t = new Date(datetime.replace ? datetime.replace(' ', 'T') : datetime).getTime()
      const diff = Math.max(0, Math.floor((Date.now() - t) / 1000))
      if (diff < 60) return 'agora há pouco'
      const m = Math.floor(diff / 60)
      if (m < 60) return `há ${m}min`
      const h = Math.floor(m / 60)
      return `há ${h}h`
    },

    /**
     * Anima a contagem do valor anterior pro novo, em ~600ms easing.
     * Usa requestAnimationFrame pra ser suave sem dependência externa.
     */
    animateTo (target, from) {
      const duration = 600
      const start = performance.now()
      const startVal = Number.isFinite(from) ? from : 0
      const delta = target - startVal

      const step = (now) => {
        const elapsed = now - start
        const t = Math.min(1, elapsed / duration)
        // ease-out cubic
        const eased = 1 - Math.pow(1 - t, 3)
        this.animatedAttended = Math.round(startVal + delta * eased)
        if (t < 1) requestAnimationFrame(step)
        else this.animatedAttended = target
      }
      requestAnimationFrame(step)
    },
    triggerBump () {
      this.bump = true
      setTimeout(() => { this.bump = false }, 450)
    }
  }
}
</script>

<style lang="scss" scoped>
.hpc-card {
  position: relative;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 5px;
  padding: 22px 26px;
  height: 100%;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(26, 29, 38, 0.04);
  color: #1a1d26;
  min-height: 180px;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 18px rgba(26, 29, 38, 0.06);
  }
}

/* Glow sutil colorido no canto — comunica vida sem invadir o card */
.hpc-bg-glow {
  position: absolute;
  top: -50%;
  right: -25%;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(85, 110, 230, 0.10) 0%, rgba(139, 92, 246, 0.06) 40%, transparent 70%);
  pointer-events: none;
  animation: hpc-breathe 6s ease-in-out infinite;
}

@keyframes hpc-breathe {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50%      { transform: scale(1.15); opacity: 1; }
}

.hpc-row {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 24px;
  min-height: 200px;
}

.hpc-now {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
}

.hpc-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.hpc-live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #34c38f;
  box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.7);
  animation: hpc-live-ring 1.8s ease-out infinite;
}

@keyframes hpc-live-ring {
  0%   { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.7); }
  70%  { box-shadow: 0 0 0 9px rgba(52, 195, 143, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0); }
}

.hpc-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #8b90a5;
  text-transform: uppercase;
}

.hpc-mode-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 6px;
  padding: 2px 8px;
  background: rgba(241, 180, 76, 0.14);
  color: #b8801f;
  border-radius: 99px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: lowercase;

  i { font-size: 12px; }
}

.hpc-prefix {
  font-size: 38px;
  font-weight: 500;
  margin-right: 4px;
  color: #8b90a5;
  vertical-align: 0.18em;
}

.hpc-value-line {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.hpc-value {
  font-size: 60px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  color: #1a1d26;
  font-variant-numeric: tabular-nums;
  transition: transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);

  &.hpc-value-bump {
    transform: scale(1.04);
    color: #556ee6;
  }
}

.hpc-fraction {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.hpc-fraction-divider {
  font-size: 38px;
  font-weight: 300;
  color: #d0d5dd;
  margin: 0 2px;
}

.hpc-fraction-total {
  font-size: 24px;
  font-weight: 700;
  color: #4a5068;
  font-variant-numeric: tabular-nums;
}

.hpc-fraction-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  color: #8b90a5;
  margin-left: 4px;
}

.hpc-value-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hpc-progress {
  height: 5px;
  background: #f0f1f6;
  border-radius: 99px;
  overflow: hidden;
  max-width: 320px;
}

.hpc-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #556ee6 0%, #34c38f 100%);
  border-radius: 99px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.hpc-stats {
  display: flex;
  align-items: stretch;
  gap: 24px;
  flex-wrap: wrap;
}

.hpc-stat {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.hpc-stat-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: grid;
  place-items: center;
  font-size: 18px;
  flex-shrink: 0;

  &.hpc-stat-icon-velocity { background: rgba(52, 195, 143, 0.12); color: #2a9d6f; }
  &.hpc-stat-icon-inside   { background: rgba(85, 110, 230, 0.12); color: #556ee6; }
  &.hpc-stat-icon-time     { background: #f0f1f6; color: #8b90a5; }
}

.hpc-stat-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.hpc-stat-value {
  font-size: 16px;
  font-weight: 700;
  color: #1a1d26;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: baseline;
  gap: 2px;

  small {
    font-size: 11px;
    font-weight: 500;
    color: #8b90a5;
  }

  &.hpc-stat-value-text {
    font-size: 13px;
    font-weight: 600;
  }
}

.hpc-stat-label {
  font-size: 11px;
  color: #8b90a5;
  font-weight: 500;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hpc-inside-prefix {
  font-weight: 500;
  color: #8b90a5;
  margin-right: 2px;
}

.hpc-gauge {
  position: relative;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

@media (max-width: 768px) {
  .hpc-row { flex-direction: column; align-items: flex-start; min-height: auto; }
  .hpc-value { font-size: 48px; }
  .hpc-gauge { align-self: center; }
  .hpc-stats { gap: 16px; }
}
</style>
